import { Component } from '@theme/component'
import { ThemeEvents } from '@theme/events'
import { morph } from '@theme/morph'
import { isMobile, mediaQueryLarge } from '@theme/use-device-detection'
import { getIOSVersion } from '@theme/utils'

export class QuickAddComponent extends Component {
  #abortController = null

  #cachedContent = new Map()

  get productPageUrl() {
    const productCard = this.closest('product-card')
    const productLink = productCard?.getProductCardLink()
    if (!productLink?.href) {
      return ''
    }
    const url = new URL(productLink.href)
    if (url.searchParams.has('variant')) {
      return url.toString()
    }
    const selectedVariantId = this.#getSelectedVariantId()
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId)
    }
    return url.toString()
  }

  #getSelectedVariantId() {
    const productCard = this.closest('product-card')
    return productCard?.getSelectedVariantId() || null
  }

  connectedCallback() {
    super.connectedCallback()
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal)
    this.#abortController?.abort()
  }

  handleClick = async (event) => {
    event.preventDefault()
    const currentUrl = this.productPageUrl
    let productGrid = this.#cachedContent.get(currentUrl)
    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl)
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]')
        if (gridElement) {
          productGrid = gridElement.cloneNode(true)
          this.#cachedContent.set(currentUrl, productGrid)
        }
      }
    }
    if (productGrid) {
      const freshContent = productGrid.cloneNode(true)
      await this.updateQuickAddModal(freshContent)
    }
    this.#openQuickAddModal()
  }

  #stayVisibleUntilDialogCloses(useDialog) {
    this.toggleAttribute('stay-visible', true)
    useDialog.addEventListener(
      'drawer:close',
      () => this.toggleAttribute('stay-visible', false),
      {
        once: true
      }
    )
  }

  #openQuickAddModal = () => {
    const useDialog = document.getElementById('quick-add-dialog')
    if (!(useDialog instanceof QuickAddDialog)) {
      return
    }
    this.#stayVisibleUntilDialogCloses(useDialog)
    useDialog.openDrawer()
  }

  #closeQuickAddModal = () => {
    const useDialog = document.getElementById('quick-add-dialog')
    if (!(useDialog instanceof QuickAddDialog)) {
      return
    }
    useDialog.closeDrawer()
  }

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) {
      return null
    }
    this.#abortController?.abort()
    this.#abortController = new AbortController()
    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal
      })
      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`
        )
      }
      const responseText = await response.text()
      const html = new DOMParser().parseFromString(responseText, 'text/html')
      return html
    } catch (error) {
      if (error.name === 'AbortError') {
        return null
      }
      throw error
    } finally {
      this.#abortController = null
    }
  }

  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content')
    if (!(productGrid && modalContent)) {
      return
    }
    if (isMobile()) {
      const productDetails = productGrid.querySelector('[data-product-details]')
      if (!productDetails) {
        return
      }
      const productFormComponent = productGrid.querySelector('product-form')
      const variantPicker = productGrid.querySelector('variant-picker')
      const productPrice = productGrid.querySelector('product-price')
      const productTitle = document.createElement('a')
      productTitle.textContent = this.dataset.productTitle || ''
      productTitle.href = this.productPageUrl
      if (
        !(productFormComponent && variantPicker && productPrice && productTitle)
      ) {
        return
      }
      const productHeader = document.createElement('div')
      productHeader.classList.add('product-header')
      productHeader.appendChild(productTitle)
      productHeader.appendChild(productPrice)
      productGrid.appendChild(productHeader)
      productGrid.appendChild(variantPicker)
      productGrid.appendChild(productFormComponent)
      productDetails.remove()
    }
    morph(modalContent, productGrid)
    this.#syncVariantSelection(modalContent)
  }

  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId()
    if (!selectedVariantId) {
      return
    }
    const modalInputs = modalContent.querySelectorAll(
      'input[type="radio"][data-variant-id]'
    )
    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId === selectedVariantId &&
        !input.checked
      ) {
        input.checked = true
        input.dispatchEvent(new Event('change', { bubbles: true }))
        break
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent)
}

class QuickAddDialog extends HTMLElement {
  #abortController = new AbortController()

  connectedCallback() {
    this.drawer = this.querySelector('use-dialog') || this.closest('use-dialog')
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal
    })
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#updateProductTitleLink
    )
    this.addEventListener('drawer:close', this.#handleDialogClose)
  }

  disconnectedCallback() {
    this.#abortController.abort()
    this.removeEventListener('drawer:close', this.#handleDialogClose)
  }

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) {
      return
    }
    this.drawer?.close?.()
  }

  #updateProductTitleLink = (event) => {
    const anchorElement = event.detail.data.html?.querySelector(
      '[data-view-product-title] a'
    )
    const viewMoreDetailsLink = this.querySelector(
      '[data-view-product-title] a'
    )
    const mobileProductTitle = this.querySelector('.product-header a')
    if (!anchorElement) {
      return
    }
    if (viewMoreDetailsLink) {
      viewMoreDetailsLink.href = anchorElement.href
    }
    if (mobileProductTitle) {
      mobileProductTitle.href = anchorElement.href
    }
  }

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion()
    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (iosVersion.major === 16 && iosVersion.minor >= 4)
    ) {
      return
    }
    requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]')
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width
        grid.style.width = `${currentWidth - 1}px`
        requestAnimationFrame(() => {
          grid.style.width = ''
        })
      }
    })
  }

  openDrawer() {
    const drawer = this.querySelector('use-dialog') || this.drawer
    drawer?.open?.()
  }

  closeDrawer() {
    const drawer = this.querySelector('use-dialog') || this.drawer
    drawer?.close?.()
  }
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog)
}
